import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Download, FileText, Phone, Bell, ShoppingBag, Sparkles, Receipt,
  ChevronRight, Loader2, CheckCircle2, XCircle, Clock, Building2, User as UserIcon,
  Search, Filter, Mail, ShieldCheck, ShieldOff,
} from "lucide-react";
import { fileTypeIcon, fileTypeLabel } from "@/lib/fileTypes";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "orders", label: "Orders" },
  { key: "downloads", label: "Downloads" },
  { key: "profile", label: "Profile" },
];

const formatINR = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const formatDate = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

export default function CustomerDashboard() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("overview");
  const [downloads, setDownloads] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get("/customer/downloads").then(({ data }) => setDownloads(data || [])),
      api.get("/orders/me").then(({ data }) => setOrders(data || [])),
      api.get("/customer/leads").then(({ data }) => setLeads(data || [])),
      api.get("/customer/notifications").then(({ data }) => setNotifs(data || [])),
    ]).finally(() => setLoading(false));
  }, []);

  const paidOrders = orders.filter(o => o.status === "paid");
  const totalSpent = paidOrders.reduce((sum, o) => sum + (o.amount || 0), 0);

  return (
    <div className="bg-slate-50/40 min-h-screen py-8" data-testid="customer-dashboard">
      <div className="nc-container">
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
          Welcome, {user?.name}
        </h1>
        <p className="text-slate-500 mt-1">Manage your downloads, orders and consultations.</p>

        <Link to="/affiliate" className="mt-6 block group" data-testid="customer-refer-cta">
          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-sky-50 p-4 md:p-5 flex items-center gap-4 hover:shadow-md transition">
            <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Refer & earn 20%</div>
              <p className="text-xs md:text-sm text-slate-600 mt-0.5">Get your unique referral link and earn 20% commission on every paid plan purchased through it.</p>
            </div>
            <span className="text-emerald-700 font-medium text-sm whitespace-nowrap group-hover:underline">Get my link →</span>
          </div>
        </Link>

        {/* Stat cards */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={Download} label="Total downloads" value={downloads.length} />
          <Stat icon={ShoppingBag} label="Paid orders" value={paidOrders.length} />
          <Stat icon={Receipt} label="Total spent" value={formatINR(totalSpent)} />
          <Stat icon={Phone} label="Consultations" value={leads.length} />
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-slate-200 flex gap-1 overflow-x-auto" role="tablist">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              role="tab" aria-selected={tab === t.key}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition ${
                tab === t.key
                  ? "border-emerald-600 text-emerald-700"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {loading && <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>}
          {!loading && tab === "overview" && <OverviewTab orders={orders} downloads={downloads} leads={leads} notifs={notifs} onJump={setTab} />}
          {!loading && tab === "orders" && <OrdersTab orders={orders} />}
          {!loading && tab === "downloads" && <DownloadsTab downloads={downloads} />}
          {!loading && tab === "profile" && <ProfileTab user={user} onSaved={refresh} />}
        </div>
      </div>
    </div>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────
function OverviewTab({ orders, downloads, leads, notifs, onJump }) {
  const recent = orders.find(o => o.status === "paid" && (o.items?.length ?? 0) > 0);
  return (
    <div className="space-y-6">
      {recent && (
        <div className="p-5 rounded-2xl bg-gradient-to-br from-emerald-50 to-sky-50 border border-emerald-200" data-testid="active-order-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-semibold text-slate-900">Most recent purchase</h3>
              <p className="text-sm text-slate-600">{recent.items.length} PDF{recent.items.length > 1 ? "s" : ""} • {formatINR(recent.amount)}</p>
              {recent.created_at && <p className="text-xs text-slate-500 mt-1">{formatDate(recent.created_at)}</p>}
            </div>
            <Link to={`/order/success?order_id=${recent.id}`} className="nc-btn-primary text-sm py-2">View & download</Link>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Recent downloads" cta={{ label: "All downloads", onClick: () => onJump("downloads") }}>
          {downloads.length === 0 ? <Empty msg="No downloads yet. Start with our free plans." /> : (
            <ul className="divide-y divide-slate-100">
              {downloads.slice(0, 6).map(d => (
                <li key={d.id} className="py-3 text-sm flex justify-between items-center">
                  <span><FileText className="inline w-4 h-4 mr-2 text-slate-400" />{fileTypeLabel(d.file_type)}</span>
                  <span className="text-xs text-slate-500">{new Date(d.timestamp).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="My consultations">
          {leads.length === 0 ? <Empty msg="No consultations yet." /> : (
            <ul className="divide-y divide-slate-100">
              {leads.slice(0, 6).map(l => (
                <li key={l.id} className="py-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">{l.source}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${l.status === "assigned" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{l.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{l.message}</div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Notifications" className="lg:col-span-2">
          {notifs.length === 0 ? <Empty msg="No notifications yet." /> : (
            <ul className="divide-y divide-slate-100">
              {notifs.map(n => (
                <li key={n.id} className="py-3 text-sm">
                  <div className="font-medium">{n.title}</div>
                  <div className="text-xs text-slate-600">{n.message}</div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

// ─── Orders ───────────────────────────────────────────────────────────────
function OrdersTab({ orders }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const filtered = useMemo(() => {
    return orders.filter(o => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (o.order_number || "").toLowerCase().includes(q) ||
        (o.id || "").toLowerCase().includes(q) ||
        (o.razorpay_payment_id || "").toLowerCase().includes(q) ||
        (o.items || []).some(it => (it.plan_title || "").toLowerCase().includes(q))
      );
    });
  }, [orders, search, statusFilter]);

  if (orders.length === 0) return <Empty msg="No orders yet. Browse our plans to make your first purchase." />;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by order #, payment ID, plan title…"
            data-testid="orders-search"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            data-testid="orders-status-filter"
            className="border border-slate-200 rounded-lg text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/30">
            <option value="all">All statuses</option>
            <option value="paid">Paid</option>
            <option value="created">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {filtered.length === 0 && <Empty msg="No orders match your filter." />}

      <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="orders-list">
        {filtered.map(o => (
          <OrderRow key={o.id} order={o} expanded={expanded === o.id} onToggle={() => setExpanded(expanded === o.id ? null : o.id)} />
        ))}
      </div>
    </div>
  );
}

function OrderRow({ order, expanded, onToggle }) {
  const [downloading, setDownloading] = useState(null);
  const [errors, setErrors] = useState({});
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const StatusBadge = () => {
    const map = {
      paid: { bg: "bg-emerald-100", fg: "text-emerald-700", icon: CheckCircle2, label: "Paid" },
      created: { bg: "bg-amber-100", fg: "text-amber-700", icon: Clock, label: "Pending" },
      failed: { bg: "bg-rose-100", fg: "text-rose-700", icon: XCircle, label: "Failed" },
    };
    const s = map[order.status] || map.created;
    const Icon = s.icon;
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${s.bg} ${s.fg}`}>
        <Icon className="w-3 h-3" /> {s.label}
      </span>
    );
  };

  const downloadItem = async (it) => {
    const k = `${it.plan_id}::${it.file_type}`;
    setDownloading(k);
    setErrors(e => ({ ...e, [k]: null }));
    try {
      const res = await api.post("/downloads", {
        plan_id: it.plan_id, file_type: it.file_type,
        name: order.billing_name || "Customer",
        mobile: order.billing_mobile || "",
        email: order.billing_email || null,
        device_fingerprint: navigator.userAgent.slice(0, 64),
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${(it.plan_title || "plan").replace(/\s+/g, "_")}_${it.file_type}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let detail = err.response?.data?.detail;
      if (err.response?.data instanceof Blob) { try { detail = JSON.parse(await err.response.data.text()).detail; } catch { } }
      setErrors(e => ({ ...e, [k]: formatApiErrorDetail(detail) || err.message }));
    } finally {
      setDownloading(null);
    }
  };

  const downloadInvoice = async () => {
    setDownloadingInvoice(true);
    try {
      const res = await api.get(`/orders/${order.id}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice_${order.order_number || order.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErrors(e => ({ ...e, _invoice: formatApiErrorDetail(err.response?.data?.detail) || err.message }));
    } finally {
      setDownloadingInvoice(false);
    }
  };

  return (
    <div data-testid={`order-row-${order.id}`}>
      <button onClick={onToggle}
        className="w-full p-4 flex items-center gap-4 hover:bg-slate-50 transition text-left"
        data-testid="order-row-toggle">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-slate-900">{order.order_number || order.id.slice(0, 12)}</span>
            <StatusBadge />
            {order.is_business && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Building2 className="w-3 h-3" /> Business</span>}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {(order.items?.length || 0)} PDF{(order.items?.length || 0) !== 1 ? "s" : ""} • {formatDate(order.created_at)}
          </p>
        </div>
        <div className="text-right">
          <div className="font-semibold text-slate-900">{formatINR(order.amount)}</div>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 bg-slate-50/60 border-t border-slate-100" data-testid={`order-detail-${order.id}`}>
          {/* Items */}
          <div className="mt-3 bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {(order.items || []).map((it, idx) => {
              const Icon = fileTypeIcon(it.file_type);
              const k = `${it.plan_id}::${it.file_type}`;
              return (
                <div key={k} className="p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{it.plan_title}</p>
                    <p className="text-xs text-slate-500">{fileTypeLabel(it.file_type)} • {formatINR(it.price_paise)}</p>
                    {errors[k] && <p className="text-xs text-rose-600 mt-1">{errors[k]}</p>}
                  </div>
                  {order.status === "paid" && (
                    <button onClick={() => downloadItem(it)} disabled={downloading === k}
                      data-testid={`order-item-download-${idx}`}
                      className="text-sm font-medium text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
                      {downloading === k ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      Download
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Totals + meta */}
          <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs text-slate-600">
            <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span>{formatINR(order.subtotal_paise)}</span></div>
              <div className="flex justify-between"><span>GST</span><span>{formatINR(order.gst_paise)}</span></div>
              <div className="flex justify-between font-semibold text-slate-900 pt-1 border-t border-slate-100"><span>Total</span><span>{formatINR(order.amount)}</span></div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-1">
              <div><span className="text-slate-400">Billing:</span> {order.billing_name}</div>
              {order.gstin && <div><span className="text-slate-400">GSTIN:</span> <span className="font-mono">{order.gstin}</span></div>}
              {order.razorpay_payment_id && <div className="truncate"><span className="text-slate-400">Payment ID:</span> <span className="font-mono text-[10px]">{order.razorpay_payment_id}</span></div>}
            </div>
          </div>

          {/* Actions */}
          {order.status === "paid" && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={downloadInvoice} disabled={downloadingInvoice}
                data-testid="order-invoice-btn"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800 bg-white border border-emerald-200 rounded-lg px-3 py-1.5">
                {downloadingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
                Tax invoice (PDF)
              </button>
              <Link to={`/order/success?order_id=${order.id}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
                Open success page
              </Link>
            </div>
          )}
          {errors._invoice && <p className="text-xs text-rose-600 mt-2">{errors._invoice}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Downloads (grouped by plan) ──────────────────────────────────────────
function DownloadsTab({ downloads }) {
  const grouped = useMemo(() => {
    const map = new Map();
    for (const d of downloads) {
      const k = d.plan_id;
      if (!map.has(k)) map.set(k, { plan_id: k, items: [] });
      map.get(k).items.push(d);
    }
    return Array.from(map.values());
  }, [downloads]);

  if (downloads.length === 0) return <Empty msg="No downloads yet. Browse plans and download your first PDF." />;

  return (
    <div className="space-y-3" data-testid="downloads-list">
      <p className="text-xs text-slate-500">
        Re-downloads of any PDF you've already downloaded are always free — pricing only applies to new purchases.
      </p>
      {grouped.map(g => (
        <PlanGroup key={g.plan_id} planId={g.plan_id} items={g.items} />
      ))}
    </div>
  );
}

function PlanGroup({ planId, items }) {
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/plans/${planId}`).then(({ data }) => { if (alive) setPlan(data); }).catch(() => { });
    return () => { alive = false; };
  }, [planId]);

  // Latest download per file_type
  const latestByType = useMemo(() => {
    const m = {};
    for (const it of items) {
      if (!m[it.file_type] || new Date(it.timestamp) > new Date(m[it.file_type].timestamp)) {
        m[it.file_type] = it;
      }
    }
    return m;
  }, [items]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 truncate">{plan?.title || `Plan ${planId.slice(0, 8)}`}</p>
          <p className="text-xs text-slate-500">
            {plan ? `${plan.length}×${plan.width} ft • ${plan.floors} floor${plan.floors > 1 ? "s" : ""} • ${plan.facing} facing` : "Loading…"}
          </p>
        </div>
        {plan && <Link to={`/plans/${plan.slug || plan.id}`} className="text-sm text-emerald-700 hover:text-emerald-800 font-medium">View plan →</Link>}
      </div>
      <div className="divide-y divide-slate-100">
        {Object.values(latestByType).map(it => (
          <DownloadFileRow key={`${planId}-${it.file_type}`} planId={planId} fileType={it.file_type} latest={it} planTitle={plan?.title} />
        ))}
      </div>
    </div>
  );
}

function DownloadFileRow({ planId, fileType, latest, planTitle }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const Icon = fileTypeIcon(fileType);

  const reDownload = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await api.post("/downloads", {
        plan_id: planId, file_type: fileType,
        name: user?.name || latest.name || "Customer",
        mobile: user?.mobile || latest.mobile || "",
        email: user?.email || null,
        device_fingerprint: navigator.userAgent.slice(0, 64),
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${(planTitle || "plan").replace(/\s+/g, "_")}_${fileType}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) { try { detail = JSON.parse(await e.response.data.text()).detail; } catch { } }
      setErr(formatApiErrorDetail(detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Icon className="w-4 h-4" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900">{fileTypeLabel(fileType)}</p>
        <p className="text-xs text-slate-500">Last downloaded {formatDate(latest.timestamp)}</p>
        {err && <p className="text-xs text-rose-600 mt-1">{err}</p>}
      </div>
      <button onClick={reDownload} disabled={busy}
        data-testid={`redownload-${planId}-${fileType}`}
        className="text-sm font-medium text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        Re-download
      </button>
    </div>
  );
}

// ─── Profile ──────────────────────────────────────────────────────────────
function ProfileTab({ user, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/orders/me").then(({ data }) => setOrders(data || [])).catch(() => { });
  }, []);

  // Distinct billing addresses across past orders, newest first
  const addresses = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const o of orders) {
      const key = [o.billing_address, o.billing_city, o.billing_state, o.billing_pincode].filter(Boolean).join("|");
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push({
        address: o.billing_address, city: o.billing_city,
        state: o.billing_state, pincode: o.billing_pincode,
        is_business: o.is_business, business_name: o.business_name, gstin: o.gstin,
        last_used: o.created_at,
      });
    }
    return out;
  }, [orders]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null); setErr(null);
    try {
      await api.put("/customer/profile", { name: name.trim(), email: email.trim() || null });
      setMsg("Profile updated");
      if (onSaved) await onSaved();
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="profile-tab">
      <Section title="Personal info">
        <form onSubmit={submit} className="space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-600">Mobile</label>
              <VerificationBadge verified={false} unverifiedLabel="Not verified" />
            </div>
            <input className="nc-input mt-1 bg-slate-50" value={user?.mobile || ""} disabled />
            <p className="text-xs text-slate-400 mt-1">Mobile is used to log in and cannot be changed here.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Full name *</label>
            <input className="nc-input mt-1" value={name} onChange={e => setName(e.target.value)} data-testid="profile-name" required />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-slate-600">Email</label>
              <EmailVerificationStatus user={user} />
            </div>
            <input type="email" className="nc-input mt-1" value={email} onChange={e => setEmail(e.target.value)} data-testid="profile-email" placeholder="NA" />
            <p className="text-xs text-slate-400 mt-1">Used for invoices, order receipts, and password resets.</p>
          </div>
          {msg && <p className="text-sm text-emerald-700">{msg}</p>}
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <button type="submit" disabled={saving} className="nc-btn-primary text-sm" data-testid="profile-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserIcon className="w-4 h-4" />}
            Save profile
          </button>
        </form>

        {user?.email && !user?.email_verified && (
          <div className="mt-5 pt-5 border-t border-slate-100">
            <VerifyEmailFlow onVerified={onSaved} />
          </div>
        )}
      </Section>

      <Section title="Saved billing addresses">
        {addresses.length === 0 ? (
          <Empty msg="No billing addresses yet. They'll appear here after your first order." />
        ) : (
          <ul className="space-y-3">
            {addresses.map((a, i) => (
              <li key={i} className="rounded-xl border border-slate-200 p-3 text-sm" data-testid={`address-${i}`}>
                {a.is_business && a.business_name && (
                  <div className="font-semibold text-slate-900 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-slate-400" />{a.business_name}</div>
                )}
                <div className="text-slate-700">{a.address}</div>
                <div className="text-slate-500 text-xs">{[a.city, a.state, a.pincode].filter(Boolean).join(", ")}</div>
                {a.gstin && <div className="text-xs text-slate-500 mt-1">GSTIN: <span className="font-mono">{a.gstin}</span></div>}
                <div className="text-xs text-slate-400 mt-1">Last used {formatDate(a.last_used)}</div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

// ─── Shared bits ──────────────────────────────────────────────────────────
function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-200">
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="text-2xl font-semibold text-slate-900 mt-2">{value}</div>
    </div>
  );
}

function Section({ title, children, className = "", cta }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-200 p-5 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-900">{title}</h3>
        {cta && <button onClick={cta.onClick} className="text-sm text-emerald-700 hover:text-emerald-800 font-medium">{cta.label} →</button>}
      </div>
      {children}
    </div>
  );
}

function Empty({ msg }) { return <p className="text-sm text-slate-400 py-4">{msg}</p>; }

// ─── Verification UI bits ────────────────────────────────────────────────
function VerificationBadge({ verified, unverifiedLabel = "Not verified", verifiedLabel = "Verified" }) {
  if (verified) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="verified-badge">
        <ShieldCheck className="w-3 h-3" /> {verifiedLabel}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="unverified-badge">
      <ShieldOff className="w-3 h-3" /> {unverifiedLabel}
    </span>
  );
}

function EmailVerificationStatus({ user }) {
  if (!user?.email) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5" data-testid="email-status-na">
        NA
      </span>
    );
  }
  return <VerificationBadge verified={!!user?.email_verified} unverifiedLabel="Not verified" />;
}

function VerifyEmailFlow({ onVerified }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailMasked, setEmailMasked] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);

  const requestOtp = async () => {
    setErr(null); setInfo(null); setSending(true);
    try {
      const { data } = await api.post("/auth/email/send-otp");
      if (data.already_verified) {
        setInfo("Email is already verified.");
        if (onVerified) await onVerified();
        return;
      }
      setEmailMasked(data.email_masked || "");
      if (data.dev_otp) {
        setInfo(`Code sent. (Dev OTP: ${data.dev_otp})`);
      } else {
        setInfo("Verification code sent. Check your inbox.");
      }
      setSent(true);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setSending(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setErr(null); setVerifying(true);
    try {
      await api.post("/auth/email/verify-otp", { code: code.trim() });
      setInfo("Email verified.");
      setSent(false); setCode("");
      if (onVerified) await onVerified();
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div data-testid="verify-email-flow">
      <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
        <Mail className="w-4 h-4 text-slate-500" /> Verify your email
      </h4>
      <p className="text-xs text-slate-500 mt-1">
        Verifying confirms you can receive invoices, order receipts and password reset links.
      </p>
      {!sent && (
        <button onClick={requestOtp} disabled={sending} className="nc-btn-secondary text-sm mt-3" data-testid="verify-email-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          Send verification code
        </button>
      )}
      {sent && (
        <form onSubmit={verify} className="mt-3 space-y-2">
          <p className="text-xs text-slate-500">
            Enter the 6-digit code{emailMasked ? <> sent to <strong className="text-slate-700">{emailMasked}</strong></> : ""}.
          </p>
          <div className="flex gap-2">
            <input
              className="nc-input flex-1 tracking-[6px] text-center font-mono"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              inputMode="numeric"
              autoFocus
              required
              data-testid="verify-email-code-input"
            />
            <button type="submit" disabled={verifying || code.length !== 6} className="nc-btn-primary text-sm whitespace-nowrap" data-testid="verify-email-submit-btn">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Verify
            </button>
          </div>
          <button type="button" onClick={requestOtp} disabled={sending} className="text-xs text-emerald-700 hover:text-emerald-800 font-medium disabled:opacity-50" data-testid="verify-email-resend-btn">
            {sending ? "Sending…" : "Resend code"}
          </button>
        </form>
      )}
      {info && <p className="text-xs text-emerald-700 mt-2" data-testid="verify-email-info">{info}</p>}
      {err && <p className="text-xs text-rose-600 mt-2" data-testid="verify-email-error">{err}</p>}
    </div>
  );
}
